// src/services/clarityService.ts
// Microsoft Clarity integration service

import clarity from '@microsoft/clarity';

/**
 * Clarity Service
 * Provides a wrapper around Microsoft Clarity for behavioral analytics
 */
class ClarityService {
  private initialized: boolean = false;
  private projectId: string | null = null;

  /**
   * Initialize Microsoft Clarity
   * @param projectId - Clarity project ID from environment variables
   */
  initialize(projectId: string): void {
    // Only initialize if not already done and projectId is valid
    if (this.initialized || !projectId || projectId.trim() === '') {
      return;
    }

    try {
      clarity.init(projectId);
      this.projectId = projectId;
      this.initialized = true;
      console.log('✅ Microsoft Clarity initialized:', projectId);
    } catch (error) {
      console.error('❌ Failed to initialize Microsoft Clarity:', error);
    }
  }

  /**
   * Check if Clarity is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Identify a user in Clarity
   * @param userId - Unique user identifier (will be hashed by Clarity)
   * @param customSessionId - Optional custom session ID
   * @param customPageId - Optional custom page ID
   * @param friendlyName - Optional friendly name for the user
   */
  identify(
    userId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string
  ): void {
    if (!this.initialized) {
      console.warn('⚠️ Clarity not initialized. Call initialize() first.');
      return;
    }

    try {
      clarity.identify(userId, customSessionId, customPageId, friendlyName);
      console.log('👤 Clarity user identified:', userId);
    } catch (error) {
      console.error('❌ Failed to identify user in Clarity:', error);
    }
  }

  /**
   * Set a custom tag in Clarity
   * @param key - Tag key
   * @param value - Tag value (string or array of strings)
   */
  setTag(key: string, value: string | string[]): void {
    if (!this.initialized) {
      console.warn('⚠️ Clarity not initialized. Call initialize() first.');
      return;
    }

    try {
      clarity.setTag(key, value);
      console.log(`🏷️ Clarity tag set: ${key} = ${value}`);
    } catch (error) {
      console.error('❌ Failed to set Clarity tag:', error);
    }
  }

  /**
   * Track a custom event in Clarity
   * @param eventName - Name of the event to track
   */
  event(eventName: string): void {
    if (!this.initialized) {
      console.warn('⚠️ Clarity not initialized. Call initialize() first.');
      return;
    }

    try {
      clarity.event(eventName);
      console.log(`📊 Clarity event tracked: ${eventName}`);
    } catch (error) {
      console.error('❌ Failed to track Clarity event:', error);
    }
  }

  /**
   * Set cookie consent status
   * @param hasConsent - Whether user has given consent (default: true)
   */
  consent(hasConsent: boolean = true): void {
    if (!this.initialized) {
      console.warn('⚠️ Clarity not initialized. Call initialize() first.');
      return;
    }

    try {
      clarity.consent(hasConsent);
      console.log(`🍪 Clarity cookie consent set: ${hasConsent}`);
    } catch (error) {
      console.error('❌ Failed to set Clarity consent:', error);
    }
  }

  /**
   * Upgrade session priority for important sessions
   * @param reason - Reason for upgrading the session
   */
  upgrade(reason: string): void {
    if (!this.initialized) {
      console.warn('⚠️ Clarity not initialized. Call initialize() first.');
      return;
    }

    try {
      clarity.upgrade(reason);
      console.log(`⬆️ Clarity session upgraded: ${reason}`);
    } catch (error) {
      console.error('❌ Failed to upgrade Clarity session:', error);
    }
  }
}

// Export singleton instance
export const clarityService = new ClarityService();

// Export type for TypeScript
export default clarityService;
